import { Tooltip } from '@bryntum/schedulerpro';
import { Dictionary } from 'lodash';
import { useEffect } from 'react';
import { User } from 'api/models';
import { useTranslate } from 'hooks/use-translation';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { fullName } from 'utils/full-name';
import { SchedulerEventType } from '../../event-types';
import { SchedulerReference } from '../../types';
import { tooltipForEvent } from '../templates/tooltips';

export const useCommonEventTooltip = (
  ref: SchedulerReference,
  userLookup: Dictionary<User>,
): void => {
  const translate = useTranslate();
  const { data: resourceManagementConfig } = useResourceManagementConfigQuery();

  useEffect(() => {
    const tooltip = new Tooltip({
      forSelector:
        '.b-sch-event.user_allocation_summary > .b-sch-event-content > :not(.time-off-icon), .b-sch-event.project-phase, .b-sch-event.team_allocation_summary, .b-sch-event.company_allocation_summary, .b-sch-event.project-timeline, .b-sch-event.demand_role, .b-sch-event.time-off-event',
      maxWidth: 312,
      getHtml: (params) => {
        const { event } = params;
        const eventId = (
          event.target.querySelector('.content') ??
          event.target.closest('.content') ??
          event.target
        )?.getAttribute('data-event-id');

        const allocationEvent =
          ref.current?.instance.eventStore.getById(eventId);

        if (allocationEvent) {
          const resource = ref.current?.instance.resourceStore.getById(
            allocationEvent.resourceId,
          );

          return tooltipForEvent(allocationEvent, translate, {
            resourceName:
              allocationEvent.get('type') === SchedulerEventType.DemandRole ||
              allocationEvent.get('type') ===
                SchedulerEventType.TeamAllocationSummary
                ? resource?.get('roleName')
                : allocationEvent.get('type') ===
                  SchedulerEventType.CompanyAllocationSummary
                ? resource?.get('companyName')
                : fullName({
                    firstName: resource?.get('firstName'),
                    lastName: resource?.get('lastName'),
                  }),
            softAllocationsEnabled:
              resourceManagementConfig?.softAllocations.enabled,
            userLookup,
          });
        }

        return null;
      },
      hoverDelay: 800,
      hideDelay: 100,
      constrainTo: ref.current.instance.timeAxisSubGridElement,
    });

    return () => {
      tooltip.destroy();
    };
  }, [
    ref,
    resourceManagementConfig?.softAllocations.enabled,
    translate,
    userLookup,
  ]);
};
